import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppConfig } from '../../utils/app-config.service';
import { Client, ClientNotification, FullClient } from '../../domain/data';
import {
    ClientInterface,
    ClientNotificationInterface,
    DocumentType,
    FullClientInterface,
    IdInterface,
    Nature,
    ValidationClient,
    ValidationClientGroup,
    ValidationStatus,
} from '../../../core';
import { LocalStorage } from '../helpers/local-storage';
import { MyHttpClient } from './my-http-client';
import { Serializer } from '../helpers/serializer/serializer';

import { mapCommonResponse } from './api-client.helpers';

import { environment } from '../../../../environments/environment';

@Injectable()
export class ClientApiClient {
    constructor(private http: MyHttpClient, private localStorage: LocalStorage, private serializer: Serializer) {}

    checkClient(client: ClientInterface, captcha: string): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.check_client).pipe(
            switchMap((url) => {
                const clients = [
                    {
                        DocumentType: String(client.docType),
                        Id: client.docId,
                        Email: client.email,
                        Phone: client.phone,
                    },
                ];

                client.legalGuardians.map((legalGuardian) => {
                    if (!legalGuardian.encryptedCode) {
                        clients.push({
                            DocumentType: String(legalGuardian.docType),
                            Id: legalGuardian.docId,
                            Email: legalGuardian.email,
                            Phone: legalGuardian.phone,
                        });
                    }
                });

                return this.http.post(url, { Clients: clients }, { headers: { 'Tower30-CaptchaToken': captcha } }).pipe(
                    map((resp: any) => {
                        if (!resp || (resp.Status !== 'OK' && resp.Status !== 'ok' && resp.Status !== '')) {
                            throw new Error(resp ? resp.Status : 'error.general');
                        } else {
                            if (resp.Result) {
                                return true;
                            } else if (!resp.Web) {
                                throw new Error('dialog.can-not-add-participant.has-agent');
                            } else {
                                throw new Error('dialog.can-not-add-participant.already-exists');
                            }
                        }
                    })
                );
            })
        );
    }

    checkEmail(email: string, clientCode?: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.check_email).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        Email: email,
                        ClientCode: clientCode ? String(clientCode) : undefined,
                    })
                    .pipe(
                        map((resp: any) => {
                            if (!resp || (resp.Status !== 'OK' && resp.Status !== 'ok' && resp.Status !== '')) {
                                throw new Error(resp ? resp.Status : 'error.general');
                            } else {
                                return resp.Respuesta === true;
                            }
                        })
                    );
            })
        );
    }

    checkPhone(phone: string, clientCode?: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.check_phone).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        Phone: phone,
                        ClientCode: clientCode ? String(clientCode) : undefined,
                    })
                    .pipe(
                        map((resp: any) => {
                            if (!resp || (resp.Status !== 'OK' && resp.Status !== 'ok' && resp.Status !== '')) {
                                throw new Error(resp ? resp.Status : 'error.general');
                            } else {
                                return resp.Respuesta === true;
                            }
                        })
                    );
            })
        );
    }

    getClientByDocumentId(docType: DocumentType, docId: string): Observable<ClientInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_client).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        DocumentId: docId,
                        DocumentType: docType,
                    })
                    .pipe(
                        map((resp: any) => {
                            if (resp && resp.Id) {
                                return this.serializer.fromJson(resp, Client);
                            }

                            throw new Error('error.general');
                        })
                    );
            })
        );
    }

    getFullClient(clientCode: IdInterface, contractCode?: IdInterface): Observable<FullClientInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_full_client).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        ClientCode: clientCode.value,
                        ContractCode: contractCode ? contractCode.value : undefined
                    })
                    .pipe(
                        map((resp: any) => {
                            if (resp && resp.NaturalClient) {
                                return this.serializer.fromJson(resp.NaturalClient, FullClient);
                            } else if (resp && resp.LegalClient) {
                                return this.serializer.fromJson(resp.LegalClient, FullClient);
                            }

                            throw new Error('error.general');
                        })
                    );
            })
        );
    }

    getValidateClientNotifications(): Observable<ClientNotificationInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_validate_client_notifications).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        Type: 'Portfolio,Client',
                    })
                    .pipe(
                        map((resp: any) => {
                            if (!resp || (resp.Status !== 'OK' && resp.Status !== 'ok' && resp.Status !== '')) {
                                throw new Error(resp ? resp.Status : 'error.general');
                            } else {
                                return this.serializer.fromJsonArray(resp.Notifications, ClientNotification);
                            }
                        })
                    );
            })
        );
    }

    rejectClient(contractCode: IdInterface, clientCode: IdInterface, message: string): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.reject_client).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                        ClientCode: String(clientCode),
                        Reason: message,
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    reviseClient(contractCode: IdInterface, clientCode: IdInterface, message: string): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.unfinalize_client).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                        ClientCode: String(clientCode),
                        Reason: message,
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    updateFullClient(client: FullClientInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.set_full_client).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        NaturalClient: client.nature === Nature.NATURAL ? this.serializer.toJson(client) : null,
                        LegalClient: client.nature === Nature.COMPANY ? this.serializer.toJson(client) : null,
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    validateClient(clientCode: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.validate_client).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        Code: String(clientCode),
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    validateClientData(clientCode: IdInterface, validations: ValidationClient[]): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.validate_client_data).pipe(
            switchMap((url) => {
                const groupsAccepted = [];

                let reasonPersonalData: string;
                let reasonProfessionalData: string;
                let reasonPublicData: string;

                validations.map((validation) => {
                    if (validation.validation.status === ValidationStatus.VALID) {
                        groupsAccepted.push(validation.group);
                    } else {
                        switch (validation.group) {
                            case ValidationClientGroup.PERSONAL_DATA:
                                reasonPersonalData = validation.validation.reason;
                                break;
                            case ValidationClientGroup.PROFESSIONAL_DATA:
                                reasonProfessionalData = validation.validation.reason;
                                break;
                            case ValidationClientGroup.PUBLIC_DATA:
                                reasonPublicData = validation.validation.reason;
                                break;
                        }
                    }
                });

                return this.http
                    .post(url, {
                        ClientCode: String(clientCode),
                        GroupsAccepted: groupsAccepted.join(','),
                        ReasonD: reasonPersonalData,
                        ReasonP: reasonProfessionalData,
                        ReasonF: reasonPublicData,
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }
}
